// English — the base locale. Every key used by a t() call must exist here;
// other locales fall back to this file for any key they omit.
// The product name "RPilot" is a brand and is never translated; where it
// appears inside a sentence the translation keeps "RPilot" verbatim.
export default {
  // Common actions
  'common.cancel': 'Cancel',
  'common.submit': 'Submit',
  'common.save': 'Save',
  'common.create': 'Create',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.retry': 'Retry',
  'common.confirm': 'Confirm',
  'common.areYouSure': 'Are you sure?',

  // Chat surface
  'chat.startConversation': 'Start a conversation',
  'chat.inputPlaceholder': 'Type a message...',
  'chat.inputPlaceholderNoCredits': 'Out of RPilot credits',
  'chat.creditsUsedUpFooter': "Your team's RPilot credits are used up. Start a new chat once credits are renewed.",
  'chat.newChat': 'New chat',
  'chat.useSkill': 'Use a skill',
  'chat.skills': 'Skills',
  'chat.loadingSkills': 'Loading skills…',
  'chat.noSkill': 'No skill',
  'chat.noSkillsAvailable': 'No skills available',
  'chat.appliesNextMessage': 'Applies from your next message',
  'chat.attachImage': 'Attach image',
  'chat.attachFile': 'Attach file',
  'chat.fileTooLarge': 'File is too large (max {size})',
  'chat.uploadFailed': 'Upload failed',
  'chat.download': 'Download',
  'chat.recordVoice': 'Record voice',
  'chat.stopRecording': 'Stop recording',
  'chat.stop': 'Stop',
  'chat.send': 'Send',
  'chat.thinking': 'Thinking…',
  'chat.thoughtProcess': 'Thought process',
  'chat.waitingForHost': 'Waiting for the dashboard…',
  'chat.thinkingComplete': 'Thinking complete',
  'chat.toolUsedOne': '1 tool used',
  'chat.toolsUsedCount': '{count} tools used',
  'chat.usedNames': 'Used {names}',
  'chat.usedToolsCount': 'Used {count} tools',
  'chat.running': 'running',
  'chat.details': 'Details',
  'chat.arguments': 'Arguments',
  'chat.result': 'Result',
  'chat.error': 'Error',
  'chat.done': 'Done',
  'chat.openInNewTab': 'Open in new tab',
  'chat.openInDocsSite': 'Open in docs site',
  'chat.noPageContent': 'No content available for this page.',
  'chat.goodResponse': 'Good response',
  'chat.badResponse': 'Bad response',
  'chat.whatWentWrong': 'What went wrong?',
  'chat.optionalFeedback': 'Optional feedback...',

  // Tool approval
  'approval.required': 'Approval required',
  'approval.requiredCount': 'Approval required — {count} actions',
  'approval.approve': 'Approve',
  'approval.deny': 'Deny',
  'approval.approveAll': 'Approve all',
  'approval.approveSelected': 'Approve selected ({count})',
  'approval.denyAll': 'Deny all',
  'approval.andMore': '…and {count} more',
  'approval.approvedChip': 'Approved',
  'approval.deniedChip': 'Denied',
  'approval.timedOutChip': 'Timed out',
  'approval.countApproved': '{count} approved',
  'approval.countDenied': '{count} denied',
  'approval.countTimedOut': '{count} timed out',
  'approval.timedOut': 'timed out',
  'approval.settled': 'settled',

  // Header / drawer
  'header.expand': 'Expand',
  'header.collapse': 'Collapse',
  'header.menu': 'Menu',
  'drawer.sessions': 'Sessions',
  'drawer.skills': 'Skills',
  'drawer.settings': 'Settings',
  'drawer.noSessions': 'No sessions yet',
  'drawer.chatFallbackTitle': 'Chat {id}',
  'drawer.newSkill': 'New Skill',
  'drawer.searchSkills': 'Search skills...',
  'drawer.noSkillsMatch': 'No skills match your search.',
  'drawer.noSkillsYet': 'No skills yet. Create one to get started.',
  'drawer.mySkills': 'My Skills',
  'drawer.builtIn': 'Built-in',
  'drawer.shared': 'Shared',
  'drawer.showToolCalls': 'Show tool calls',
  'drawer.autoAcceptEdits': 'Auto-accept edits',
  'drawer.darkMode': 'Dark Mode',
  'drawer.version': 'Version',
  'drawer.versionCopied': 'Copied',

  // Credit gate / usage / exhaustion
  'gate.meetTitle': 'Meet RPilot',
  'gate.meetBody': 'RPilot is your AI assistant for RDrive. Ask anything about your project data in plain language, and get clear answers with direct links. Use Agent skills to automate tasks and streamline your workflow.',
  'gate.meetCta': 'Want RPilot for your team? Contact our sales team',
  'gate.outOfCreditsTitle': "You're out of RPilot credits",
  'gate.outOfCreditsBody': "Your team's RPilot credits are used up. Get in touch to renew and pick up right where you left off.",
  'credits.title': 'Credit Usage',
  'credits.loadFailed': "Couldn't load credit usage.",
  'credits.active': 'Active',
  'credits.fallback': 'Fallback',
  'credits.exhausted': 'exhausted',
  'credits.noPools': 'No credit pools',
  'credits.daily': 'Daily',
  'credits.weekly': 'Weekly',
  'credits.monthly': 'Monthly',
  'credits.dayShort': 'D',
  'credits.weekShort': 'W',
  'credits.monthShort': 'M',
  'credits.usedNoLimit': '{label} — {spent} used (no limit)',
  'credits.balanceLine': 'Balance: {balance} credits · resets at midnight {zone}',
  'credits.account': 'Account',
  'credits.scopeProject': 'Project',
  'credits.scopeTeam': 'Team',
  'credits.scopeMixed': 'Mixed',
  'credits.scopePool': 'Pool',
  'credits.messageNotSent': "Your team's RPilot credits are used up, so this message couldn't be sent. Your conversation is saved.",
  'credits.contactUs': 'Contact us',

  // Contact sales
  'sales.contactSales': 'Contact sales',
  'sales.requestReceived': 'Request received ✓',
  'sales.submitAgain': 'Submit again',
  'sales.name': 'Name',
  'sales.workEmail': 'Work email',
  'sales.companyTeam': 'Company / team (optional)',
  'sales.message': 'Message (optional)',
  'sales.nameRequired': 'Name is required',
  'sales.emailRequired': 'Work email is required',
  'sales.emailInvalid': 'Enter a valid email address',
  'sales.thanks': 'Thanks — our team will be in touch.',
  'sales.alreadyReceived': "We've already received your request — we'll be in touch.",
  'sales.genericError': 'Something went wrong. Please try again.',
  'sales.speakWithSales': 'Speak with Sales',
  'sales.salesChatPrefill': "Hi, I'd like to speak with sales about RPilot for my team.",

  // Auth gate

  // Skill editor
  'skill.newSkill': 'New Skill',
  'skill.editTitle': 'Edit — {name}',
  'skill.viewTitle': '{name}',
  'skill.readOnlyHint': 'This is a built-in skill maintained by RDrive and cannot be edited. To customise it for this project, create a shared skill with the same name — it will take precedence here.',
  'skill.name': 'Name',
  'skill.nameHint': 'kebab-case identifier',
  'skill.nameRule': 'Must be kebab-case (e.g. review-rfi)',
  'skill.shared': 'Shared',
  'skill.description': 'Description',
  'skill.descriptionPlaceholder': 'What this skill does and when to use it',
  'skill.required': 'Required',
  'skill.tags': 'Tags',
  'skill.tagsPlaceholder': 'Type and press Enter',
  'skill.allowedTools': 'Allowed Tools',
  'skill.allToolsAllowed': 'All tools allowed',
  'skill.instructions': 'Instructions (Markdown)',
  'skill.contentRequired': 'Content is required',
  'skill.deleteTitle': 'Delete Skill',
  'skill.deleteMessage': 'Delete skill "{name}"? This cannot be undone.',

  // Location picker
  'picker.selectLocation': 'Select Location',
  'picker.searchDrawings': 'Search drawings...',
  'picker.planViewerFailed': "Couldn't load the plan viewer",
  'picker.selectDrawing': 'Select a drawing to view',
  'picker.tapToPlace': 'Tap a location pin to place the issue',
  'picker.pinPlaced': 'Pin placed',

  // Markdown editor toolbar
  'md.bold': 'Bold',
  'md.italic': 'Italic',
  'md.heading': 'Heading',
  'md.inlineCode': 'Inline code',
  'md.codeBlock': 'Code block',
  'md.link': 'Link',
  'md.bulletList': 'Bullet list',
  'md.numberedList': 'Numbered list',
  'md.blockquote': 'Blockquote',
  'md.nothingToPreview': 'Nothing to preview',
}
